'use client';
import { useInViewport } from 'ahooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TourItem } from '../types';

const HIGH_LIGHT_CLASSNAME = 'tour-highlight';

/**
 * 解析类名字符串，支持空格分隔的多个类名
 * @param className 类名字符串，可以是单个类名或多个空格分隔的类名
 * @returns 类名数组
 */
const parseClassNames = (className: string | undefined | null): string[] => {
  if (!className) return [];
  return className.trim().split(/\s+/).filter(Boolean);
};

interface UseTourOptions {
  /** Tour 配置 */
  config: {
    /** 引导项列表 */
    items: TourItem[];
    /** 是否显示底部按钮 */
    showFooter?: boolean;
  };
  onOpen?: () => void;
  /**
   * 关闭回调
   * type: 'manual' | 'complete'
   * manual: 点击关闭按钮
   * complete: 步骤完成自动关闭
   */
  onClose?: (type?: 'manual' | 'complete') => void;
}

export const useTour = ({ config, onOpen, onClose }: UseTourOptions) => {
  const [step, setStep] = useState(-1);
  const anchorRef = useRef<HTMLDivElement>(null);

  const tourItem = useMemo(() => config.items[step], [config.items, step]); // 当前引导项

  const [inViewport] = useInViewport(anchorRef);

  const totalSteps = config.items.length;

  // 处理目标元素高亮和定位
  useEffect(() => {
    const { domId, targetStyle, isInHeader, highlightClassName, disableHighLight, scrollElementId } = tourItem ?? {};
    if (!domId) return;

    const targetDom = document.getElementById(domId);
    if (!targetDom || !anchorRef.current) return;

    // 解析并添加高亮边框效果（支持多个类名）
    const classNamesToAdd = !disableHighLight ? parseClassNames(highlightClassName ?? HIGH_LIGHT_CLASSNAME) : [];
    classNamesToAdd.forEach((className) => {
      targetDom.classList.add(className);
    });

    let originalZIndex = 'auto';
    let headerDom: HTMLElement | null = null;

    // 如果目标元素在header中，header的z-index是30，会阻碍tour-highlight中设置的50，所以需要设置header的z-index为auto
    if (isInHeader) {
      headerDom = document.getElementById('header');
      if (headerDom) {
        originalZIndex = headerDom.style.zIndex;
        headerDom.style.zIndex = 'auto';
      }
    }

    // 应用自定义样式
    const originalStyles = new Map<string, string>();
    if (targetStyle) {
      // 保存原始样式并应用新样式
      Object.entries(targetStyle).forEach(([key, value]) => {
        // 将驼峰式属性名转换为kebab-case (如 borderRadius 转为 border-radius)
        const kebabKey = key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
        originalStyles.set(kebabKey, targetDom.style.getPropertyValue(kebabKey));
        targetDom.style.setProperty(kebabKey, String(value));
      });
    }
    // 确保目标元素被看到
    if (scrollElementId) {
      const scrollElementDom = document.getElementById(scrollElementId);
      scrollElementDom?.scrollIntoView({ behavior: 'instant', block: 'center' });
    } else {
      targetDom.scrollIntoView({ behavior: 'instant', block: 'center' });
    }

    // 将锚点定位到目标元素
    const updateAnchorPosition = () => {
      const rect = targetDom.getBoundingClientRect();

      // 根据目标元素位置设置锚点位置
      if (!anchorRef.current) return;

      anchorRef.current.style.left = `${rect.left}px`;
      anchorRef.current.style.top = `${rect.top}px`;
      anchorRef.current.style.width = `${rect.width}px`;
      anchorRef.current.style.height = `${rect.height}px`;
    };

    // 初始定位
    updateAnchorPosition();

    // 监听滚动和调整大小事件
    window.addEventListener('resize', updateAnchorPosition);
    window.addEventListener('scroll', updateAnchorPosition, true);

    return () => {
      // 移除所有添加的高亮类名
      if (!disableHighLight) {
        const classNamesToRemove = parseClassNames(highlightClassName ?? HIGH_LIGHT_CLASSNAME);
        classNamesToRemove.forEach((className) => {
          targetDom.classList.remove(className);
        });
      }

      // 恢复原始样式
      if (targetStyle) {
        originalStyles.forEach((value, key) => {
          targetDom.style.setProperty(key, value);
        });
      }
      if (headerDom) headerDom.style.zIndex = originalZIndex;

      window.removeEventListener('resize', updateAnchorPosition);
      window.removeEventListener('scroll', updateAnchorPosition, true);
    };
  }, [tourItem]);

  useEffect(() => {
    if (inViewport) {
      onOpen?.();
    }
  }, [inViewport, onOpen]);

  const goNext = useCallback(() => {
    if (step === totalSteps - 1) {
      onClose?.('complete');
      setStep(-1);
    } else {
      setStep((prev) => prev + 1);
    }
  }, [step, totalSteps, onClose]);

  const goPrev = useCallback(() => {
    setStep((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const closeTour = useCallback(
    (type: 'manual' | 'complete' = 'manual') => {
      onClose?.(type);
      setStep(-1);
    },
    [onClose],
  );

  // 跳转到指定步骤
  const goToStep = useCallback(
    (step = 0) => {
      if (step >= 0 && step < totalSteps) {
        setStep(step);
      }
    },
    [totalSteps],
  );

  return {
    // 状态
    step,
    totalSteps,
    tourItem,
    anchorRef,
    // 配置
    showFooter: config.showFooter,
    // 方法
    closeTour,
    goNext,
    goPrev,
    goToStep,
  };
};
